import { getFunctions, httpsCallable } from 'firebase/functions';
import { getCurrentApp } from '../config/firebaseManager';

// Initialize Firebase Functions (lazy initialization)
let functions: ReturnType<typeof getFunctions> | null = null;

const getFunctionsInstance = () => {
  if (!functions) {
    functions = getFunctions(getCurrentApp());
  }
  return functions;
};

// Lazy-load callable functions to ensure Firebase is initialized
const getCallableFunction = <T = any>(name: string) => {
  return httpsCallable<T, any>(getFunctionsInstance(), name);
};

// Export callable function creators
// All admin functions are prefixed with 'admin' (camelCase) to avoid conflicts with glisten-app functions
export const createPaymentIntent = getCallableFunction('adminCreatePaymentIntent');
export const processRefund = getCallableFunction('adminProcessRefund');
export const getPaymentIntent = getCallableFunction('adminGetPaymentIntent');
export const listRefunds = getCallableFunction('adminListRefunds');
export const createOrRetrieveCustomer = getCallableFunction('adminCreateOrRetrieveCustomer');
export const sendPaymentLinkEmail = getCallableFunction('adminSendPaymentLinkEmail');

// Types for function responses
export interface PaymentIntentResponse {
  success: boolean;
  paymentIntent: {
    id: string;
    clientSecret: string;
    amount: number;
    currency: string;
    status: string;
  };
  paymentLink?: {
    id: string;
    url: string;
  } | null;
}

export interface RefundResponse {
  success: boolean;
  refund: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    reason: string;
    created: number;
  };
}

export interface PaymentIntentDetailsResponse {
  success: boolean;
  paymentIntent: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    customer: string | null;
    metadata: Record<string, string>;
    created: number;
    charges?: Array<{
      id: string;
      amount: number;
      status: string;
      refunded: boolean;
      refunds?: Array<{
        id: string;
        amount: number;
        status: string;
        reason: string;
        created: number;
      }>;
    }>;
  };
}

export interface RefundsListResponse {
  success: boolean;
  refunds: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    reason: string;
    created: number;
    metadata: Record<string, string>;
  }>;
}

export interface CustomerResponse {
  success: boolean;
  customer: {
    id: string;
    email: string | null;
    name: string | null;
    phone: string | null;
    created: number;
    metadata: Record<string, string>;
  };
}

// Wrapper functions with proper typing
export const secureStripeService = {
  // Create a payment intent
  async createPaymentIntent(data: {
    amount: number;
    currency?: string;
    customerId?: string;
    bookingId: string;
    metadata?: Record<string, string>;
  }): Promise<PaymentIntentResponse> {
    const result = await createPaymentIntent(data);
    return result.data as PaymentIntentResponse;
  },

  // Process a refund
  async processRefund(data: {
    paymentIntentId: string;
    amount: number;
    reason?: string;
    metadata?: Record<string, string>;
  }): Promise<RefundResponse> {
    const result = await processRefund(data);
    return result.data as RefundResponse;
  },

  // Get payment intent details
  async getPaymentIntent(paymentIntentId: string): Promise<PaymentIntentDetailsResponse> {
    const result = await getPaymentIntent({ paymentIntentId });
    return result.data as PaymentIntentDetailsResponse;
  },

  // List refunds for a payment intent
  async listRefunds(paymentIntentId: string): Promise<RefundsListResponse> {
    const result = await listRefunds({ paymentIntentId });
    return result.data as RefundsListResponse;
  },

  // Create or retrieve a customer
  async createOrRetrieveCustomer(data: {
    email: string;
    name?: string;
    phone?: string;
    metadata?: Record<string, string>;
  }): Promise<CustomerResponse> {
    const result = await createOrRetrieveCustomer(data);
    return result.data as CustomerResponse;
  },

  // Send payment link email
  async sendPaymentLinkEmail(data: {
    customerEmail: string;
    customerName?: string;
    amount: number;
    paymentLinkUrl: string;
    description?: string;
  }): Promise<{success: boolean; message: string}> {
    const result = await sendPaymentLinkEmail(data);
    return result.data as {success: boolean; message: string};
  },
}; 